import React, { useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import jsonData from "../../_library/courses.json";
import BannerSliderCard from "../../components/BannerSliderCard";
import BannerSliderThumb from "../../components/BannerSliderThumb";

// Pure utility - defined outside component so it never changes
const shuffleArray = (array) => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [
      shuffledArray[j],
      shuffledArray[i],
    ];
  }
  return shuffledArray;
};

const Banner = ({ addToCart, isUSDCurrency }) => {
  const [hovered, setHovered] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [data, setData] = useState([]);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [playingId, setPlayingId] = useState(null);

  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.85);

  const handleDetailsButtonClick = () => {
    setIsVideoPlaying(false);
    setPlayingId(null);
  };

  // Memoized so its reference is stable → satisfies exhaustive-deps
  const getData = useCallback(async () => {
    const rawData = (await jsonData) && jsonData.CoursesCollection;
    const shuffled = shuffleArray(rawData || []);
    setData(shuffled.slice(0, 5));
  }, []); // empty deps because it only uses stable setData + imported jsonData + outer shuffleArray

  useEffect(() => {
    getData();
  }, [getData]); // Now ESLint is happy

  const handlePlayVideo = (courseId) => {
    const dotsContainer = document.getElementsByClassName("slick-dots")[0];
    if (dotsContainer) {
      const target = dotsContainer.querySelector(
        `[data-course-id="${courseId}"]`,
      );
      if (target) target.click();
    }

    setHovered(true);
    setHoveredId(courseId);

    if (playingId === courseId) {
      // Same thumb clicked again → toggle play/pause (resume if was paused)
      setIsVideoPlaying(!isVideoPlaying);
    } else {
      // Different thumb → switch and play (old one auto-pauses via useEffect in Card)
      setPlayingId(courseId);
      setIsVideoPlaying(true);
    }
  };

  const handlePauseVideo = (courseId) => {
    if (playingId === courseId) {
      setIsVideoPlaying(false);
      // IMPORTANT: Do NOT setPlayingId(null) here.
      // Keeping playingId allows:
      // - Thumb to stay highlighted with "Now Playing" badge
      // - Card to keep video layer visible after pause (big Play overlay ready)
      // - Easy resume by clicking play on Thumb or overlay (handlePlayVideo sees same id → toggles isVideoPlaying to true)
      // playingId is only cleared in handleDetailsButtonClick or when a different video is played.
    }
  };

  const onMouseEnterHandler = (event, id) => {
    event.currentTarget.click();
    setHovered(true);
    setHoveredId(id);
    if (playingId !== id) {
      setIsVideoPlaying(false);
    }
  };

  const onMouseRemoveHandler = () => {
    setHovered(false);
    setHoveredId(null);
  };

  const settings = {
    customPaging: function (i) {
      return (
        <div
          data-course-id={data[i].CourseId}
          onMouseEnter={(e) => onMouseEnterHandler(e, data[i].CourseId)}
          onMouseLeave={() => onMouseRemoveHandler()}
        >
          <BannerSliderThumb
            item={data[i]}
            key={`Bannerthumb${data[i].CourseId}`}
            isUSDCurrency={isUSDCurrency}
            addToCart={addToCart}
            onDetailsButtonClick={handleDetailsButtonClick}
            onPlayVideo={handlePlayVideo}
            onPauseVideo={handlePauseVideo}
            playingId={playingId}
            isVideoPlaying={isVideoPlaying}
          />
        </div>
      );
    },
    dots: true,
    dotsClass: "slick-dots slick-thumb",
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  return (
    <div className="banner-section">
      <h2>Custom Paging</h2>
      <Slider {...settings}>
        {data &&
          data.map((item) => (
            <div key={item?.CourseId}>
              <BannerSliderCard
                item={item}
                hovered={hovered}
                hoveredId={hoveredId}
                isVideoPlaying={isVideoPlaying}
                playingId={playingId}
                isMuted={isMuted}
                setIsMuted={setIsMuted}
                volume={volume}
                setVolume={setVolume}
                onPauseVideo={handlePauseVideo}
                onPlayVideo={handlePlayVideo}
              />
            </div>
          ))}
      </Slider>
    </div>
  );
};

export default Banner;
